import {
  CalendarOutlined,
  EditOutlined,
  PlusOutlined,
  ProjectOutlined,
  SearchOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Card, Input, Progress, Steps, Tag } from "antd";
import { FC, ReactNode, useState } from "react";
import MemberAvatar from "./components/MemberAvatar";
import ContentDueRiskPanel, {
  ContentDueRiskFocus,
} from "./components/ContentDueRiskPanel";
import ProjectContentItemList, {
  collectProjectOverdueContentItems,
  ProjectContentItemFocus,
} from "./components/ProjectContentItemList";
import ProjectOverdueBadge from "./components/ProjectOverdueBadge";
import TaskLeaderboard, {
  WeeklyTaskRanking,
} from "./components/TaskLeaderboard";
import ProjectRequirementCardSummary from "./components/ProjectRequirementCardSummary";
import {
  ProjectFlowLayouts,
  ProjectPhase,
  ProjectPhasePermissions,
  ProjectPhaseSummaries,
  ProjectProgressStepStatus,
  getOngoingProjects,
} from "./projectPhase";
import type {
  TaskPriority,
} from "./components/TaskPriorityTag";
import { searchProjectOverviewProjects } from "./projectOverviewSearch";
import { useDebouncedValue } from "./useDebouncedValue";

type DashboardView = "workspace" | "board" | "profile";
interface ApiProjectMember {
  user_id: string;
  role: string;
  joined_at: number;
}

interface ApiProjectItem {
  id?: string;
  project_id: string;
  project_name: string;
  template: "phased" | "single";
  creator: string;
  team_code: string;
  team_admin_id?: string;
  normalized_project_name?: string;
  members: ApiProjectMember[];
  avatar?: string;
  status: "planning" | "active" | "blocked" | "done";
  task_summary?: {
    total: number;
    completed: number;
    pending: number;
  };
  phase_permissions: ProjectPhasePermissions;
  phase_summaries: ProjectPhaseSummaries;
  flow_layouts: ProjectFlowLayouts;
  flow_graph_versions: Record<ProjectPhase, number>;
  remark?: string;
  created_at?: number;
  updated_at?: number;
}

interface ApiTaskItem {
  id?: string;
  project_id: string;
  phase: ProjectPhase;
  creator: string;
  assignees: string[];
  predecessor_ids: string[];
  title: string;
  content?: string;
  priority?: "low" | "medium" | "high" | "urgent";
  due_time?: number;
  created_at?: number;
  status: "todo" | "doing" | "blocked" | "review" | "done" | "discarded";
  subtask_summary?: {
    items?: Array<{
      id?: string;
      title: string;
      status: "todo" | "transfer" | "review" | "rejected" | "done";
      assignees: string[];
      reviewers: string[];
      content_items: Array<{
        item_id: string;
        assignees: string[];
        content: string;
        completed: boolean;
        start_time: number;
        due_time: number;
        priority: TaskPriority;
      }>;
      start_time: number;
      due_time: number;
      priority: TaskPriority;
    }>;
  };
}

interface TeamMember {
  username?: string;
  nickname?: string;
  mail?: string;
  phone?: string;
  avatar?: string;
  department?: string[];
  team_code?: string;
  role?: number;
}

interface MemberDashboardProps {
  activeView: DashboardView;
  apiProjects: ApiProjectItem[];
  canCreateProject: boolean;
  canEditProject: (project: ApiProjectItem) => boolean;
  copyText: (value: string) => void;
  department: string[];
  dashboardLoading: boolean;
  canArchiveProject: (project: ApiProjectItem) => boolean;
  canUnarchiveProject: (project: ApiProjectItem) => boolean;
  getCurrentTask: (tasks: ApiTaskItem[]) => ApiTaskItem | undefined;
  getMemberDisplayName: (username: string) => string;
  getProjectFallbackColor: (projectName: string) => string;
  getProjectMemberSummary: (project: ApiProjectItem) => string;
  getProjectStepCurrent: (project: ApiProjectItem) => number;
  getProjectStepStatuses: (
    project: ApiProjectItem
  ) => ProjectProgressStepStatus[];
  getTaskStatusClass: (status: ApiTaskItem["status"]) => string;
  getTaskStatusText: (status: ApiTaskItem["status"]) => string;
  getTextAvatar: (text: string) => { initial: string; background: string };
  isMutationPending: (key: string) => boolean;
  nickname: string;
  onCreateProject: () => void;
  onArchiveProject: (project: ApiProjectItem) => void;
  onDeleteProject: (project: ApiProjectItem) => void;
  onEditProject: (project: ApiProjectItem) => void;
  onOpenMemberCalendar: (member: TeamMember) => void;
  onOpenDueRiskItem: (focus: ContentDueRiskFocus) => void;
  onOpenProjectContentItem: (focus: ProjectContentItemFocus) => void;
  onOpenProfileModal: () => void;
  onOpenPasswordModal: () => void;
  onOpenProjectFlow: (project: ApiProjectItem) => void;
  onOpenWeekPlan: (open: boolean) => void;
  onUnarchiveProject: (project: ApiProjectItem) => void;
  projectStepNames: string[];
  teamTasks: ApiTaskItem[];
  teamMembers: TeamMember[];
  teamCode: string;
  userId: string;
  userAvatar: string;
  userMail: string;
  userPhone: string;
  userRole: number;
  weekPlanContent: ReactNode;
  weeklyPending: number;
  weeklyRanking: WeeklyTaskRanking | null;
}

const MemberDashboard: FC<MemberDashboardProps> = ({
  activeView,
  apiProjects,
  canCreateProject,
  canEditProject,
  copyText,
  department,
  dashboardLoading,
  canArchiveProject,
  canUnarchiveProject,
  getMemberDisplayName,
  getProjectFallbackColor,
  getProjectMemberSummary,
  getProjectStepCurrent,
  getProjectStepStatuses,
  getTextAvatar,
  isMutationPending,
  nickname,
  onCreateProject,
  onArchiveProject,
  onDeleteProject,
  onEditProject,
  onOpenMemberCalendar,
  onOpenDueRiskItem,
  onOpenProjectContentItem,
  onOpenProfileModal,
  onOpenPasswordModal,
  onOpenProjectFlow,
  onOpenWeekPlan,
  onUnarchiveProject,
  projectStepNames,
  teamTasks,
  teamMembers,
  teamCode,
  userId,
  userAvatar,
  userMail,
  userPhone,
  userRole,
  weekPlanContent,
  weeklyPending,
  weeklyRanking,
}) => {
  const [projectSearchText, setProjectSearchText] = useState("");
  const debouncedProjectSearchText = useDebouncedValue(projectSearchText);
  const isManager = userRole === 1;
  const workspaceProjects = getOngoingProjects(apiProjects);
  const workspaceProjectSearchResults = searchProjectOverviewProjects(
    workspaceProjects,
    teamTasks,
    debouncedProjectSearchText,
    getMemberDisplayName,
    (assigneeId) => isManager || assigneeId === userId
  );
  const activeProjects = workspaceProjects;
  const myProjects = [...apiProjects].sort((prev, next) => {
    const prevDone = prev.status === "done";
    const nextDone = next.status === "done";
    if (prevDone !== nextDone) {
      return prevDone ? 1 : -1;
    }
    return (next.updated_at || next.created_at || 0) - (prev.updated_at || prev.created_at || 0);
  });
  const canDeleteProject = (project: ApiProjectItem) =>
    project.creator === userId;
  const isProjectParticipant = (project: ApiProjectItem) =>
    project.creator === userId ||
    project.members.some((member) => member.user_id === userId);
  const visibleDepartmentMembers = isManager
    ? teamMembers.filter((member) => {
        const memberDepartments = member.department || [];
        return department.some((item) => memberDepartments.includes(item));
      })
    : [];
  const visibleDepartmentNames = Array.from(
    new Set(department.map((item) => item.trim()).filter(Boolean))
  );
  const memberGroups = visibleDepartmentNames.map((departmentName) => ({
    departmentName,
    members: visibleDepartmentMembers.filter((member) =>
      (member.department || []).includes(departmentName)
    ),
  }));

  const renderProjectTaskFlows = (
    project: ApiProjectItem,
    preferredAssigneeId?: string
  ) => {
    return (
      <ProjectContentItemList
        getMemberDisplayName={getMemberDisplayName}
        includeAll={isManager}
        projectId={project.project_id}
        projectName={project.project_name}
        projectTemplate={project.template}
        preferredAssigneeId={preferredAssigneeId}
        tasks={teamTasks}
        userId={userId}
        onOpenContentItem={onOpenProjectContentItem}
      />
    );
  };

  const renderWorkspaceProjects = () => (
    <Card className="workspacePanel projectOverviewPanel">
      <div className="workspacePanel__header">
        <h3>{isManager ? "团队项目" : "我的项目"}</h3>
        <div className="projectOverviewPager">
          <Input
            allowClear
            aria-label="搜索项目或参与成员"
            className="projectOverviewSearch"
            onChange={(event) => setProjectSearchText(event.target.value)}
            placeholder="搜索项目或参与成员"
            prefix={<SearchOutlined />}
            value={projectSearchText}
          />
          <span>
            {dashboardLoading
              ? "加载中"
              : debouncedProjectSearchText.trim()
              ? `${workspaceProjectSearchResults.length} / ${workspaceProjects.length} 个项目`
              : `${workspaceProjects.length} 个项目`}
          </span>
        </div>
      </div>
      {workspaceProjectSearchResults.length > 0 ? (
        <div className="projectOverviewGrid">
          {workspaceProjectSearchResults.map(
            ({ project, matchedAssigneeId }) => {
            const overdueContentItems = collectProjectOverdueContentItems(
              teamTasks,
              project.project_id
            );
            const overdueContentCount = overdueContentItems.length;
            return (
              <div
                aria-label={
                  overdueContentCount
                    ? `${project.project_name}，${overdueContentCount} 项任务内容逾期`
                    : project.project_name
                }
                className={`projectOverviewMiniCard projectOverviewMiniCard--clickable${
                  overdueContentCount
                    ? " projectOverviewMiniCard--overdue"
                    : ""
                }`}
                key={project.project_id}
                onClick={() => onOpenProjectFlow(project)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpenProjectFlow(project);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className="projectOverviewMiniCard__header">
                  {project.avatar ? (
                    <img
                      alt={project.project_name}
                      className="projectAvatar"
                      src={project.avatar}
                    />
                  ) : (
                    <div
                      className="projectAvatar projectAvatar--fallback"
                      style={{
                        background: getProjectFallbackColor(project.project_name),
                      }}
                    >
                      {project.project_name.slice(0, 1)}
                    </div>
                  )}
                  <div>
                    <div className="projectOverviewMiniCard__titleRow">
                      <h4>{project.project_name}</h4>
                      {overdueContentCount > 0 && (
                        <ProjectOverdueBadge
                          getMemberDisplayName={getMemberDisplayName}
                          items={overdueContentItems}
                          projectName={project.project_name}
                        />
                      )}
                    </div>
                    <span>负责人：{getMemberDisplayName(project.creator)}</span>
                    <span>项目成员：{getProjectMemberSummary(project)}</span>
                  </div>
                </div>
                {renderProjectTaskFlows(project, matchedAssigneeId)}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="projectEmpty">
          <ProjectOutlined />
          <h3>{workspaceProjects.length ? "没有匹配的项目" : "暂无参与项目"}</h3>
          <p>
            {workspaceProjects.length
              ? "换个项目名或成员昵称试试。"
              : "当你被加入项目后，这里会展示你的项目和参与的子任务。"}
          </p>
        </div>
      )}
    </Card>
  );

  const renderDepartmentMembers = () => {
    if (!isManager) {
      return null;
    }

    return (
      <Card className="workspacePanel workspacePanel--wide">
        <div className="workspacePanel__header">
          <h3>部门成员</h3>
          <span>{visibleDepartmentMembers.length} 人</span>
        </div>
        <div className="departmentMemberGroups">
          {memberGroups.length ? (
            memberGroups.map((group) => (
              <div className="departmentMemberGroup" key={group.departmentName}>
                <div className="departmentMemberGroup__header">
                  <strong>{group.departmentName}</strong>
                  <span>{group.members.length} 人</span>
                </div>
                {group.members.length ? (
                  <div className="teamMemberGrid">
                    {group.members.map((member) => (
                      <div
                        className="teamMemberCard teamMemberCard--readonly"
                        key={`${group.departmentName}-${member.username}`}
                      >
                        <MemberAvatar
                          avatar={member.avatar}
                          displayName={
                            member.nickname || member.username || "成员"
                          }
                          fallback={getTextAvatar(
                            member.nickname || member.username || "成员"
                          )}
                        />
                        <div>
                          <div className="teamMemberCard__title">
                            <div>
                              <strong>
                                {member.nickname || member.username || "未命名成员"}
                              </strong>
                              <span>{member.username}</span>
                            </div>
                            <div className="teamMemberCard__titleActions">
                              <button
                                className="teamMemberCard__calendar"
                                onClick={() => onOpenMemberCalendar(member)}
                                title="查看近一个月任务日历"
                                type="button"
                              >
                                <CalendarOutlined />
                              </button>
                              <Tag
                                className={
                                  member.role === 1
                                    ? "teamMemberRole teamMemberRole--admin"
                                    : "teamMemberRole"
                                }
                              >
                                {member.role === 1 ? "管理员" : "成员"}
                              </Tag>
                            </div>
                          </div>
                          <div className="teamMemberCard__tags">
                            {(member.department || []).length ? (
                              (member.department || []).map((item) => (
                                <Tag key={item}>{item}</Tag>
                              ))
                            ) : (
                              <Tag>未分配</Tag>
                            )}
                          </div>
                          <div className="teamMemberCard__footer">
                            <small>点击日历查看近 30 天任务模块</small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="departmentMemberGroup__empty">暂无成员</div>
                )}
              </div>
            ))
          ) : (
            <div className="departmentMemberGroup__empty">
              暂无可查看的同部门成员
            </div>
          )}
        </div>
      </Card>
    );
  };

  const renderMyProjects = (showToolbar: boolean) => (
    <section className="boardLayout">
      {showToolbar && (
        <div className="boardToolbar">
          <div>
            <h2>{isManager ? "团队项目" : "我的项目"}</h2>
            <p>
              {isManager
                ? "展示当前团队的全部项目；未参与的项目以只读方式查看。"
                : "展示你参与的全部项目，包括进行中、已完成和已归档项目。"}
            </p>
          </div>
          {canCreateProject && (
            <Button icon={<PlusOutlined />} onClick={onCreateProject} type="primary">
              新建项目
            </Button>
          )}
        </div>
      )}

      {myProjects.length > 0 ? (
        <div className="projectGrid">
          {myProjects.map((project) => (
            <Card
              key={project.project_id}
              className={
                project.status === "done"
                  ? "projectCard projectCard--clickable projectCard--archived"
                  : "projectCard projectCard--clickable"
              }
              onClick={() => onOpenProjectFlow(project)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onOpenProjectFlow(project);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="projectCard__header">
                <div className="projectCard__identity">
                  {project.avatar ? (
                    <img
                      alt={project.project_name}
                      className="projectAvatar"
                      src={project.avatar}
                    />
                  ) : (
                    <div
                      className="projectAvatar projectAvatar--fallback"
                      style={{
                        background: getProjectFallbackColor(project.project_name),
                      }}
                    >
                      {project.project_name.slice(0, 1)}
                    </div>
                  )}
                    <div>
                      <h3>{project.project_name}</h3>
                      <span>
                        产品经理：{getMemberDisplayName(project.creator) || "未记录"}
                      </span>
                    <span style={{ marginLeft: 12 }}>
                      参与成员：{getProjectMemberSummary(project)}
                    </span>
                  </div>
                </div>
                {((isManager && isProjectParticipant(project)) ||
                  canDeleteProject(project) ||
                  canEditProject(project)) && (
                  <div className="projectCard__actions">
                    {canEditProject(project) && (
                      <Button
                        className="projectCard__editButton"
                        icon={<EditOutlined />}
                        onClick={(event) => {
                          event.stopPropagation();
                          onEditProject(project);
                        }}
                        size="small"
                      >
                        编辑
                      </Button>
                    )}
                    {isManager && isProjectParticipant(project) && (
                      <Button
                        className="projectCard__archiveButton"
                        disabled={
                          !canUnarchiveProject(project) &&
                          !canArchiveProject(project)
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          if (canUnarchiveProject(project)) {
                            onUnarchiveProject(project);
                            return;
                          }
                          onArchiveProject(project);
                        }}
                        loading={isMutationPending(
                          `project:${
                            canUnarchiveProject(project)
                              ? "unarchive"
                              : "archive"
                          }:${project.project_id}`
                        )}
                        size="small"
                        type={canUnarchiveProject(project) ? "default" : "primary"}
                      >
                        {canUnarchiveProject(project) ? "撤销归档" : "归档"}
                      </Button>
                    )}
                    {canDeleteProject(project) && (
                      <Button
                        className="projectCard__deleteButton"
                        danger
                        onClick={(event) => {
                          event.stopPropagation();
                          onDeleteProject(project);
                        }}
                        size="small"
                      >
                        删除
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <div className="projectCard__progress">
                <span>当前进度</span>
                {project.template === "single" ? (
                  <div className="projectCard__singleProgress">
                    <div>
                      <strong>单任务视图</strong>
                      <span>
                        已完成 {project.task_summary?.completed || 0}，待完成{" "}
                        {project.task_summary?.pending || 0}
                      </span>
                    </div>
                    <Progress
                      percent={
                        project.task_summary?.total
                          ? Math.round(
                              ((project.task_summary.completed || 0) /
                                project.task_summary.total) *
                                100
                            )
                          : 0
                      }
                      showInfo
                      size="small"
                      status={project.status === "done" ? "success" : "active"}
                    />
                  </div>
                ) : (
                  <Steps
                    current={getProjectStepCurrent(project)}
                    size="small"
                  >
                    {projectStepNames.map((step, stepIndex) => (
                      <Steps.Step
                        key={step}
                        status={getProjectStepStatuses(project)[stepIndex]}
                        title={step}
                      />
                    ))}
                  </Steps>
                )}
              </div>

              <ProjectRequirementCardSummary requirement={project.remark} />
            </Card>
          ))}
        </div>
      ) : (
        <div className="projectEmpty">
          <ProjectOutlined />
          <h3>{isManager ? "团队暂无项目" : "暂无参与项目"}</h3>
          <p>
            {isManager
              ? "当前团队还没有创建任何项目。"
              : canCreateProject
              ? "你可以新建项目，或等待被加入已有项目。"
              : "当你被加入项目后，这里会展示你的项目卡片。"}
          </p>
        </div>
      )}
    </section>
  );

  if (activeView === "workspace") {
    return (
      <>
        <TaskLeaderboard
          activeProjectCount={activeProjects.length}
          currentUserId={userId}
          getTextAvatar={getTextAvatar}
          onWeekPlanOpenChange={onOpenWeekPlan}
          pendingTaskCount={weeklyPending}
          ranking={weeklyRanking}
          weekPlanContent={weekPlanContent}
        />
        <ContentDueRiskPanel
          getMemberDisplayName={getMemberDisplayName}
          includeAll={isManager}
          onOpenItem={onOpenDueRiskItem}
          projects={workspaceProjects}
          tasks={teamTasks}
          userId={userId}
        />
        {renderWorkspaceProjects()}
        {renderDepartmentMembers()}
      </>
    );
  }

  if (activeView === "board") {
    return renderMyProjects(true);
  }

  return (
    <section className="profileGrid">
      <Card className="workspacePanel profileCard">
        <div className="profileCard__hero">
          <div className="profileCard__avatar">
            <Avatar
              size={72}
              src={userAvatar || undefined}
              style={
                userAvatar
                  ? undefined
                  : {
                      background: getTextAvatar(nickname || userId).background,
                    }
              }
            >
              {!userAvatar && getTextAvatar(nickname || userId).initial}
            </Avatar>
            <button onClick={onOpenProfileModal} type="button">
              修改头像
            </button>
          </div>
          <div>
            <h3>{nickname || userId}</h3>
            <span>{userId}</span>
          </div>
        </div>
        <div className="profileInfoList">
          <div className="profileInfoItem">
            <span>邮箱</span>
            <strong>{userMail || "未填写邮箱"}</strong>
            <Button
              onClick={onOpenProfileModal}
              size="small"
              type="link"
            >
              编辑
            </Button>
          </div>
          <div className="profileInfoItem">
            <span>昵称</span>
            <strong>{nickname || "未填写昵称"}</strong>
            <Button onClick={onOpenProfileModal} size="small" type="link">
              编辑
            </Button>
          </div>
          <div className="profileInfoItem">
            <span>电话</span>
            <strong>{userPhone || "---"}</strong>
            <Button onClick={onOpenProfileModal} size="small" type="link">
              编辑
            </Button>
          </div>
          <div className="profileInfoItem">
            <span>密码</span>
            <strong>已设置</strong>
            <Button onClick={onOpenPasswordModal} size="small" type="link">
              修改
            </Button>
          </div>
          <div className="profileInfoItem">
            <span>部门</span>
            <div className="profileDepartmentTags">
              {department.length ? (
                department.map((item) => <Tag key={item}>{item}</Tag>)
              ) : (
                <strong>未填写部门</strong>
              )}
            </div>
          </div>
          <div className="profileInfoItem">
            <span>团队码</span>
            <strong>{teamCode || "未记录"}</strong>
            <Button
              disabled={!teamCode}
              onClick={() => copyText(teamCode)}
              size="small"
              type="link"
            >
              复制
            </Button>
          </div>
        </div>
      </Card>

      <Card className="workspacePanel">
        <div className="workspacePanel__header">
          <h3>个人偏好</h3>
          <SettingOutlined />
        </div>
        <ul className="workspaceBulletList">
          <li>默认查看工作台概览</li>
          <li>接收项目里程碑提醒</li>
          <li>任务更新同步到个人消息中心</li>
        </ul>
      </Card>
    </section>
  );
};

export default MemberDashboard;
